import { uniqueId } from "lodash";
import { FC, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastOptions } from "react-toastify";

import { NewActionMenu } from "@app/shared/action-menu";
import { PublishProjectsModal } from "@app/network-portfolio-management/components/PublishProjectsModal";
import { ArchiveProjectLink } from "@app/projects/network-assessment/ArchiveProjectLink";
import { EditProjectOptionsLink } from "@app/projects/network-assessment/EditProjectOptionsLink";
import JobsPanelContentLink from "@app/projects/network-assessment/JobsPanelContentLink";
import { ZipJobsPanelContentLink } from "@app/projects/network-assessment/ZipJobsPanelContentLink";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserInfo } from "@app/user/UserContext";
import {
  ProjectActionMenuCallbacks,
  ProjectActionMenuModel,
  ResourceShareLocationState,
} from "@app/projects/network-assessment/projectActionMenuTypes";

export interface SharedProjectActionMenuProps {
  /** Owner projects get the full action menu; non-owners (shared) get the limited viewer menu. */
  projectOwned: boolean;
  /** The view-agnostic project model (id, name, status flags) read by both the owner and viewer menus. */
  model: ProjectActionMenuModel;
  /** Renders the dropdown with position: fixed to escape overflow contexts (e.g. inside a table). */
  fixMenu?: boolean;
  /** A scrollable ancestor's ref; the menu flips upward before running past its bottom edge. */
  dropupContainer?: React.RefObject<HTMLElement>;

  /* ---- Owner menu (required when projectOwned) ---- */
  callbacks?: ProjectActionMenuCallbacks;
  /** Pre-built router state for the resource sharing page (breadcrumb trail differs per view). */
  shareLocationState?: ResourceShareLocationState;
  /** The authenticated user — required by the Jobs panel. */
  user?: UserInfo;
  /** Marks the project read-only after a run is triggered from the Project Options flyout. */
  setProjectIsReadOnly?: (projectIds: number[]) => void;

  /* ---- Viewer menu (used when !projectOwned) ---- */
  /** Toggles the Hide/Unhide label. */
  showingHidden?: boolean;
  /** Hide/unhide handler. When omitted, the viewer menu shows only Project Options (view-only). */
  onHidden?: () => void;
}

/**
 * The per-project action menu extracted from the classic ProjectActionMenu so the classic and Network
 * Portfolio Management dashboards share one implementation. Owners get Archive, Clone, Project Options,
 * Share/Unshare, Jobs and an optional Cancel Project Execution; non-owners (shared projects) get the
 * limited viewer menu (Project Options view-only, plus Hide/Unhide when an {@link onHidden} handler is
 * supplied). It reuses the existing action-link components unchanged.
 */
const toastOptions: ToastOptions = { closeOnClick: true, containerId: "z-index-9999" };

export const SharedProjectActionMenu: FC<SharedProjectActionMenuProps> = ({
  projectOwned,
  fixMenu,
  dropupContainer,
  model,
  callbacks,
  shareLocationState,
  user,
  setProjectIsReadOnly,
  showingHidden,
  onHidden,
}) => {
  const dotsMenuRef = useRef<HTMLButtonElement>(null);
  const sidePanelId = useRef(uniqueId("side-panel-")).current;
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handlePublish = async (comment: string): Promise<void> => {
    if (!callbacks?.onPublish) { return; }
    setIsPublishing(true);
    try {
      await callbacks.onPublish(comment);
      toast.success("Project published.", toastOptions);
    } catch (error) {
      const status = error !== null && typeof error === "object" && "status" in error
        ? (error as { status: unknown }).status : undefined;
      if (status === 409) {
        toast.warn("Project is Read Only.", toastOptions);
      } else {
        toast.error("An error occurred while publishing. Please try again.", toastOptions);
      }
    } finally {
      setIsPublishing(false);
      setPublishModalOpen(false);
    }
  };

  const renderOwnerMenu = () => {
    if (!callbacks || !shareLocationState || !user) {
      return null;
    }

    const archiveIcon = model.isArchived ? null : QesIcons.Archive;
    const archiveText = model.isArchived ? "Unarchive" : "Archive";
    const archiveDisabled = !model.dataReady || model.isRunning || (model.isReadOnly && !model.isArchived);
    const publishDisabled = !model.isPublishable || model.isReadOnly;

    // Cancel-execution lives here (opt-in via model.isQueued) so the classic and NPM adapters don't each
    // re-wire it; NPM never queues a project, so it never surfaces the item. Calls the service directly to
    // avoid a projects -> home import cycle.
    const cancelProjectExecution = async () => {
      try {
        await new ProjectService().cancelQueuedProject(model.projectId);
        toast.info("Project execution canceled.", toastOptions);
      } catch {
        toast.error("An error occurred while attempting to cancel project execution.", toastOptions);
      }
    };

    return (
      <>
        {callbacks.onPublish && !model.isArchived && (
          <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN]}>
            <QESButton
              qesType="none"
              qesTestId={`action-menu-item--publish-${model.projectId}`}
              className="col-md-12 action-menu-item d-block fw-normal"
              disabled={publishDisabled}
              onClick={() => setPublishModalOpen(true)}
            >
              <span className={`project-action-button${publishDisabled ? " disabled" : ""}`}>
                <QesIcon iconName={QesIcons.UploadV1} isDecorative />
                Publish
              </span>
            </QESButton>
          </RoleRestricted>
        )}
        <ArchiveProjectLink
          archiveDisabled={archiveDisabled}
          archiveText={archiveText}
          archiveIcon={archiveIcon}
          onArchiveConfirm={callbacks.onArchiveConfirm}
        />
        <Link
          to={`/projects/${model.projectId}/clone`}
          className="d-block col-12 action-menu-item side-panel-link fw-normal">
          <span className="project-action-button"><QesIcon iconName={QesIcons.CopyV7} isDecorative />Clone</span>
        </Link>
        <EditProjectOptionsLink
          containerId={sidePanelId}
          disabled={!model.dataReady}
          projectId={model.projectId}
          onUpdateProjectOptions={callbacks.onUpdateProjectOptions}
          setProjectIsReadOnly={setProjectIsReadOnly}
          triggerElementOverride={dotsMenuRef}
        />
        <div className="col-md-12 action-menu-item">
          <Link
            data-testid={`project-action__menu--${model.projectId}-share-unshare`}
            to={{
              pathname: `/resources/share`,
              state: shareLocationState,
            }}
          >
            <span className="project-action-button"><QesIcon iconName={QesIcons.ShareV6} isDecorative />Share/Unshare</span>
          </Link>
        </div>
        <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
          {model.isZipAnalysis
            ? <ZipJobsPanelContentLink projectId={model.projectId} projectName={model.name} triggerElementOverride={dotsMenuRef} />
            : <JobsPanelContentLink
                containerId={sidePanelId}
                projectId={model.projectId}
                projectName={model.name}
                triggerElementOverride={dotsMenuRef}
                user={user} />
          }
        </RoleRestricted>
        {model.isQueued && (
          <QESButton
            qesType="none"
            qesTestId={`action-menu-item--cancel-project-execution`}
            className={`col-md-12 action-menu-item d-block fw-normal`}
            onClick={() => { cancelProjectExecution(); }}
          >
            <span className="project-action-button">
              <QesIcon
                iconName={QesIcons.X}
                isDecorative
              />
              Cancel Project Execution
            </span>
          </QESButton>
        )}
      </>
    );
  };

  const renderViewerMenu = () => (
    <>
      <EditProjectOptionsLink
        containerId={sidePanelId}
        disabled={true}
        projectId={model.projectId}
      />
      {onHidden && (
        <QESButton
          qesType="none"
          qesTestId="project-action-menu__hide"
          onClick={onHidden}
          className="action-menu-item fw-normal col-12 d-block"
        >
          <span className="project-action-button">
            <QesIcon
              className="align-text-top"
              iconName={QesIcons.EyeOff}
              isDecorative
            />
            {showingHidden ? "Unhide" : "Hide"}
          </span>
        </QESButton>
      )}
    </>
  );

  return (
    <>
      <NewActionMenu
        alignRight={true}
        buttonClassNames="p-0 text-dark btn-icon btn-sm"
        containerClassnames="project-action-menu-container"
        buttonText={<QesIcon iconName={QesIcons.DotsVertical} description="Open project actions menu" />}
        hideArrow={true}
        testId={`project-tile__menu--${model.projectId}`}
        fixMenu={fixMenu}
        dropupContainer={dropupContainer}
        fixedMenuOffset={{
          x: 8,
          y: 0,
        }}
        triggerElementOverride={dotsMenuRef}
      >
        {projectOwned ? renderOwnerMenu() : renderViewerMenu()}
      </NewActionMenu>
      {projectOwned && callbacks?.onPublish && (
        <PublishProjectsModal
          open={publishModalOpen}
          projects={[{ name: model.name, projectId: model.projectId }]}
          totalCount={1}
          isPublishing={isPublishing}
          onCancel={() => setPublishModalOpen(false)}
          onPublish={handlePublish}
        />
      )}
    </>
  );
};
